import { c as _c } from "react/compiler-runtime";
import { addHours, startOfHour, subDays } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import { Route, useHistory } from 'react-router';
import useSWR from 'swr';
import { OutpostDestination, OutpostDestinationTypeMetadata, OutpostTenant } from '../../../../../../../typings/outpost';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { formatNumberShorthand } from '../../../../utils';
import { stringifyQuery } from '../../../../utils';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import { StyledCard } from '../../../common/base/Card';
import Icon from '../../../common/base/Icon';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import DisplayDate from '../../../common/DisplayDate';
import { useFieldsSuggestions } from '../../../common/Form/Fields/hooks/useEventPayloadFieldsSuggestions';
import { Div } from '../../../common/helpers/StyledUtils';
import { buildPreviewBuckets, GROUP_METRICS_BY_2H, GROUP_METRICS_BY_4H, PreviewMetricEntry } from '../../../common/Histogram/PreviewHistogram';
import { insertPathEllipsis } from '../../../common/JSONBrowser';
import Table from '../../../common/Table/Table';
import { parseDynamicColumn } from '../../../contexts/TableContext';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import useOutpostMetricsQuery from '../../../hooks/useOutpostMetricsQuery';
import { StyledViewSplitPaneContent, StyledViewSplitPaneLayout, StyledViewSplitPaneSection, StyledViewSplitPaneSidebar, StyledViewSplitPaneSidebarBody, ViewMetadataCopyable, ViewMetadataDate, ViewMetadataItem, ViewSplitPaneContentHeader, ViewSplitPaneSidebarHeader, ViewSplitPaneSidebarSection } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../../DashboardScene/DashboardContext';
import CreateDestinationModal from '../Destinations/CreateDestinationModal';
import DestinationMetricsPreview from '../Destinations/DestinationMetricsPreview';
import { DestinationTypeIcon } from '../Destinations/DestinationTypeIcon';
import { TopicsList, TopicsOverview } from '../Topics';
interface ParsedField {
  field_path: string;
  path: (string | number)[];
  display_name: string;
}
const POPULAR_FIELD_NAMES = ['id', 'email', 'name', 'key', 'type', 'status', 'user_id', 'customer_id'];
const LATEST_EVENTS_PAGE_SIZE = 10;
const now = addHours(startOfHour(new Date()), 1);
const parseFieldPath = (field_path: string): ParsedField | null => {
  const parts = field_path.split('.');
  const root = parts[0];
  const path_parts = parts.slice(1);
  const path: (string | number)[] = [];
  for (const part of path_parts) {
    const array_match = part.match(/^(.+)\[\*\]$/);
    if (array_match) {
      path.push(array_match[1]);
    } else {
      path.push(part);
    }
  }
  return {
    field_path,
    path,
    display_name: `${root}.${path.join('.')}`
  };
};
const getNestedValue = (obj: unknown, path: (string | number)[]): unknown => {
  if (path.length === 0) return obj;
  const [first, ...rest] = path;
  if (!obj || typeof obj !== 'object') return undefined;
  return getNestedValue((obj as Record<string | number, unknown>)[first], rest);
};

// Module-scope helper: the try/catch lives here (outside the component) so the React
// Compiler can memoize TenantOverview — it bails on any component whose body contains a
// try/catch statement.
const renderDynamicColumnField = (column_string: string, row_key: string, source: {
  data?: unknown;
  metadata?: unknown;
} | null | undefined) => {
  try {
    const parsed = parseDynamicColumn(column_string);
    let data: unknown;
    if (parsed.root === 'data') {
      data = source?.data || {};
    } else if (parsed.root === 'metadata') {
      data = source?.metadata || {};
    } else {
      return <Text key={`${row_key}-${column_string}`} muted size="xs">
          -
        </Text>;
    }
    const value = getNestedValue(data, parsed.path);
    const value_string = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (value === undefined || value === null) {
      return <Text key={`${row_key}-${column_string}`} muted size="xs">
          -
        </Text>;
    }
    return <Tooltip key={`${row_key}-${column_string}`} side="bottom" fill disabled={value_string.length <= 25} delay={500} element={<Div style={{
      maxHeight: '300px',
      width: '300px',
      overflow: 'auto'
    }} p={{
      x: 1
    }}>
            <Text size="xs" semi muted m={{
        b: 1
      }}>
              Payload
            </Text>
            <Text mono size="xs">
              {value_string}
            </Text>
          </Div>}>
        <Text mono size="xs" ellipsis>
          {value_string}
        </Text>
      </Tooltip>;
  } catch {
    return <Text key={`${row_key}-${column_string}`} muted size="xs">
        -
      </Text>;
  }
};
const SuggestedColumnHeader: React.FC<{
  full_path: string;
  width: number;
}> = t0 => {
  const $ = _c(16);
  const {
    full_path,
    width
  } = t0;
  let t1;
  if ($[0] !== full_path) {
    t1 = <Text mono={true} muted={true} size="xs">{full_path}</Text>;
    $[0] = full_path;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "center",
      justify: "space-between",
      gap: 1
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      grow: true
    };
    t4 = {
      minWidth: 0
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  const t5 = (width - 20) / 8;
  let t6;
  if ($[5] !== full_path || $[6] !== t5) {
    t6 = insertPathEllipsis(full_path, t5);
    $[5] = full_path;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <Div flex={t3} style={t4}><Text mono={true} muted={true} size="xs" ellipsis={true}>{t6}</Text></Div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Icon icon="auto_awesome" small={true} muted={true} />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7) {
    t9 = <Div flex={t2} w={100}>{t7}{t8}</Div>;
    $[11] = t7;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t1 || $[14] !== t9) {
    t10 = <Tooltip delay={500} tooltip={t1} side="top">{t9}</Tooltip>;
    $[13] = t1;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
};
export const TenantOverview = t0 => {
  const $ = _c(121);
  const {
    tenant
  } = t0;
  const history = useHistory();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const [latest_events_current_page, setLatestEventsCurrentPage] = useState(1);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [latest_events_cursor, setLatestEventsCursor] = useState(t1);
  const latest_events_filters = {
    tenant_id: tenant.id,
    limit: LATEST_EVENTS_PAGE_SIZE,
    order_by: "time",
    dir: "desc" as const,
    next: latest_events_cursor.next,
    prev: latest_events_cursor.prev
  };
  let t2;
  if ($[1] !== tenant.id) {
    t2 = APIMethodKeys.outpost.destinations.list(tenant.id);
    $[1] = tenant.id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== HookdeckAPI.outpost.destinations || $[4] !== tenant.id) {
    t3 = () => HookdeckAPI.outpost.destinations.list(tenant.id);
    $[3] = HookdeckAPI.outpost.destinations;
    $[4] = tenant.id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: destinations,
    mutate: mutate_destinations,
    isLoading: destinations_loading
  } = useSWR(t2, t3);
  let t4;
  bb0: {
    if (!destinations || destinations.length === 0) {
      t4 = null;
      break bb0;
    }
    let t5;
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = ["count"];
      t6 = ["destination_id", "status"];
      $[6] = t5;
      $[7] = t6;
    } else {
      t5 = $[6];
      t6 = $[7];
    }
    let t7;
    let t8;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = subDays(now, 1).toISOString();
      t8 = now.toISOString();
      $[8] = t7;
      $[9] = t8;
    } else {
      t7 = $[8];
      t8 = $[9];
    }
    let t9;
    if ($[10] !== t7) {
      t9 = {
        start: t7,
        end: t8
      };
      $[10] = t7;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    let t10;
    if ($[12] !== destinations) {
      t10 = destinations.map(_temp);
      $[12] = destinations;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    let t11;
    if ($[14] !== t10) {
      t11 = {
        destination_id: t10
      };
      $[14] = t10;
      $[15] = t11;
    } else {
      t11 = $[15];
    }
    let t12;
    if ($[16] !== t11 || $[17] !== t9) {
      t12 = {
        endpoint: "attempts" as const,
        measures: t5,
        dimensions: t6,
        granularity: "1h",
        time: t9,
        filters: t11
      };
      $[16] = t11;
      $[17] = t9;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    t4 = t12;
  }
  const dest_metrics_query = t4;
  const {
    raw_data: dest_raw_data
  } = useOutpostMetricsQuery(dest_metrics_query);
  let t5;
  let timeseries_2h_by_destination_id;
  if ($[19] !== dest_raw_data || $[20] !== destinations) {
    const dest_entries = dest_raw_data?.map(_temp2) ?? null;
    const dest_ids = (destinations || []).map(_temp3);
    timeseries_2h_by_destination_id = dest_entries && buildPreviewBuckets(dest_ids, dest_entries, GROUP_METRICS_BY_2H, now);
    t5 = dest_entries && buildPreviewBuckets(dest_ids, dest_entries, GROUP_METRICS_BY_4H, now);
    $[19] = dest_raw_data;
    $[20] = destinations;
    $[21] = t5;
    $[22] = timeseries_2h_by_destination_id;
  } else {
    t5 = $[21];
    timeseries_2h_by_destination_id = $[22];
  }
  const timeseries_4h_by_destination_id = t5;
  let t6;
  if ($[23] !== dest_raw_data) {
    t6 = (obj, t7) => {
      const {
        id: id_1
      } = t7;
      obj[id_1] = dest_raw_data?.reduce((acc, entry) => {
        if (entry?.["Attempts.destination_id"] === id_1) {
          acc = acc + parseInt(entry?.["Attempts.count"] || "0", 10);
        }
        return acc;
      }, 0);
      return obj;
    };
    $[23] = dest_raw_data;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const count_by_destination_id = (destinations || []).reduce(t6, {} as Record<string, number | undefined>);
  const {
    data: latest_events_response,
    isLoading: latest_events_response_loading
  } = useSWR(APIMethodKeys.outpost.events.list(latest_events_filters), () => HookdeckAPI.outpost.events.list(latest_events_filters));
  let t7;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = APIMethodKeys.outpost.destinationTypes.list();
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== HookdeckAPI.outpost.destinationTypes) {
    t8 = () => HookdeckAPI.outpost.destinationTypes.list();
    $[26] = HookdeckAPI.outpost.destinationTypes;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const {
    data: destination_types
  } = useSWR(t7, t8);
  const latest_events = latest_events_response?.models || [];
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setLatestEventsCurrentPage(1);
      setLatestEventsCursor({});
    };
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== tenant.id) {
    t10 = [tenant.id];
    $[29] = tenant.id;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  useEffect(t9, t10);
  const {
    suggestions,
    getFieldWidth
  } = useFieldsSuggestions({
    entity: "outpost_events",
    models: latest_events
  });
  let t11;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const [recently_used_fields] = useLocalStorage("suggested-columns-recent-outpost_events", t11);
  let t12;
  if ($[32] !== destination_types) {
    t12 = (destination_types || []).reduce(_temp4, {} as Record<string, OutpostDestinationTypeMetadata>);
    $[32] = destination_types;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const destination_types_by_type = t12;
  let t13;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = ["Type", "Target", "Topics", "Deliveries", "Created At"];
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const headers = t13;
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [{
      min: 152,
      max: 152
    }, {
      min: 150
    }, {
      min: 150
    }, {
      min: 140
    }, {
      min: 152,
      max: 152
    }];
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const widths = t14;
  const has_popular_name = _temp5;
  let t15;
  if ($[36] !== recently_used_fields) {
    t15 = field_0 => {
      const recently_used_index = recently_used_fields.indexOf(field_0.field_path);
      if (recently_used_index !== -1) {
        return 1000 + (recently_used_fields.length - recently_used_index);
      }
      if (has_popular_name(field_0)) {
        return 500;
      }
      return 0;
    };
    $[36] = recently_used_fields;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const get_priority_score = t15;
  let t16;
  if ($[38] !== get_priority_score) {
    t16 = (a, b) => get_priority_score(b) - get_priority_score(a);
    $[38] = get_priority_score;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const latest_event_columns = suggestions.map(parseFieldPath).filter(_temp6).sort(t16).slice(0, 3).map(_temp7);
  let t17;
  bb1: {
    if (latest_event_columns.length === 0) {
      t17 = 200;
      break bb1;
    }
    let t18;
    if ($[40] !== getFieldWidth) {
      t18 = (max_width, column_string) => {
        const parsed = parseDynamicColumn(column_string);
        return Math.max(max_width, getFieldWidth(parsed.display_name));
      };
      $[40] = getFieldWidth;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    const max_suggested_width = latest_event_columns.reduce(t18, 0);
    t17 = Math.max(200, Math.min(400, max_suggested_width));
  }
  const latest_event_suggested_column_width = t17;
  let t18;
  if ($[42] !== latest_event_suggested_column_width) {
    t18 = column_string_0 => {
      const parsed_0 = parseDynamicColumn(column_string_0);
      return <SuggestedColumnHeader key={column_string_0} full_path={parsed_0.display_name} width={latest_event_suggested_column_width} />;
    };
    $[42] = latest_event_suggested_column_width;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const latest_event_headers = ["Time", "Topic", ...latest_event_columns.map(t18)];
  const max_topic_length = latest_events.reduce(_temp8, 0);
  const latest_event_topic_width = Math.max(200, Math.min(400, max_topic_length * 8));
  let t19;
  if ($[44] !== latest_event_suggested_column_width) {
    t19 = () => ({
      min: latest_event_suggested_column_width,
      max: latest_event_suggested_column_width
    });
    $[44] = latest_event_suggested_column_width;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const latest_event_widths = [{
    min: 152,
    max: 152
  }, {
    min: latest_event_topic_width,
    max: latest_event_topic_width
  }, ...latest_event_columns.map(t19)];
  const rows = (destinations || []).map(destination => ({
    id: destination.id,
    fields: [<Div key={`${destination.id}-type`} flex={{
      align: "center",
      gap: 1.5
    }}><DestinationTypeIcon type={destination.type} type_meta={destination_types_by_type[destination.type]} /><Text size="s" semi={true} capitalize={true}>{destination_types_by_type[destination.type]?.label || destination.type}</Text></Div>, destination.target, <TopicsList key={`${destination.id}-topics`} topics={destination.topics} />, <Div key={`${destination.id}-deliveries`} flex={{
      align: "center",
      gap: 2
    }} min_w={0}><Text w={{
        px: 40
      }} muted={true} number={true}>{formatNumberShorthand(count_by_destination_id?.[destination.id] || 0)}</Text><DestinationMetricsPreview data_2h_sample={timeseries_2h_by_destination_id?.[destination.id] || []} data_4h_sample={timeseries_4h_by_destination_id?.[destination.id] || []} destination={destination} /></Div>, <DisplayDate key={`${destination.id}-created`} date={destination.created_at} show_timezone={false} />]
  }));
  const latest_event_rows = latest_events.map(event_0 => ({
    id: event_0.id,
    fields: [<DisplayDate key={`${event_0.id}-time`} date={event_0.time} show_timezone={false} />, <Text key={`${event_0.id}-topic`} mono={true} size="s" ellipsis={true}>{event_0.topic}</Text>, ...latest_event_columns.map(column_string_1 => renderDynamicColumnField(column_string_1, event_0.id, event_0))]
  }));
  const t20 = `/tenants/${tenant.id}/settings`;
  let t21;
  if ($[46] !== t20) {
    t21 = <ViewSplitPaneSidebarHeader title="Tenant details" action={<Button outline={true} icon="edit" to={t20}>Edit</Button>} />;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== tenant.id) {
    t22 = <ViewMetadataItem label="Tenant ID"><ViewMetadataCopyable value={tenant.id} /></ViewMetadataItem>;
    $[48] = tenant.id;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== tenant.destinations_count) {
    t23 = <ViewMetadataItem label="Destination count"><Text mono={true} ellipsis={true}>{tenant.destinations_count}</Text></ViewMetadataItem>;
    $[50] = tenant.destinations_count;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== tenant.topics) {
    t24 = <ViewMetadataItem label="Subscribed topics"><TopicsOverview topics={tenant.topics} /></ViewMetadataItem>;
    $[52] = tenant.topics;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t22 || $[55] !== t23 || $[56] !== t24) {
    t25 = <ViewSplitPaneSidebarSection>{t22}{t23}{t24}</ViewSplitPaneSidebarSection>;
    $[54] = t22;
    $[55] = t23;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== tenant.metadata) {
    t26 = tenant.metadata && Object.entries(tenant.metadata).map(_temp9);
    $[58] = tenant.metadata;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== tenant.created_at) {
    t27 = <ViewMetadataItem label="Created at"><ViewMetadataDate date={tenant.created_at} /></ViewMetadataItem>;
    $[60] = tenant.created_at;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== tenant.updated_at) {
    t28 = <ViewMetadataItem label="Last updated"><ViewMetadataDate date={tenant.updated_at} /></ViewMetadataItem>;
    $[62] = tenant.updated_at;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t26 || $[65] !== t27 || $[66] !== t28) {
    t29 = <ViewSplitPaneSidebarSection title="Metadata" bordered={true}>{t26}{t27}{t28}</ViewSplitPaneSidebarSection>;
    $[64] = t26;
    $[65] = t27;
    $[66] = t28;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t21 || $[69] !== t25 || $[70] !== t29) {
    t30 = <StyledViewSplitPaneSidebar><StyledViewSplitPaneSidebarBody>{t21}{t25}{t29}</StyledViewSplitPaneSidebarBody></StyledViewSplitPaneSidebar>;
    $[68] = t21;
    $[69] = t25;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  const t31 = destinations?.length;
  let t32;
  if ($[72] !== t31) {
    t32 = <Badge neutral={true} subtle={true}>{t31}</Badge>;
    $[72] = t31;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  const t33 = `/tenants/${tenant.id}/add-destination`;
  let t34;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      scroll: false
    };
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== t33) {
    t35 = <Button.Permission outline={true} icon="add_circle" to={{
      pathname: t33,
      state: t34
    }}>Add destination</Button.Permission>;
    $[75] = t33;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t32 || $[78] !== t35) {
    t36 = <ViewSplitPaneContentHeader title="Event destinations" actions={<>{t32}{t35}</>} />;
    $[77] = t32;
    $[78] = t35;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  const t37 = destinations?.length;
  let t38;
  if ($[80] !== history || $[81] !== tenant.id) {
    t38 = destination_id => {
      history.push(`/tenants/${tenant.id}/destinations/${destination_id}`);
    };
    $[80] = history;
    $[81] = tenant.id;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== destinations_loading || $[84] !== headers || $[85] !== rows || $[86] !== t37 || $[87] !== t38 || $[88] !== widths) {
    t39 = <StyledCard overflow_hidden={true}><Table headers={headers} widths={widths} rows={rows} loading={destinations_loading} current_count={t37} footer={false} onRowSelected={t38} /></StyledCard>;
    $[83] = destinations_loading;
    $[84] = headers;
    $[85] = rows;
    $[86] = t37;
    $[87] = t38;
    $[88] = widths;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  let t40;
  if ($[90] !== t36 || $[91] !== t39) {
    t40 = <StyledViewSplitPaneSection>{t36}{t39}</StyledViewSplitPaneSection>;
    $[90] = t36;
    $[91] = t39;
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  let t41;
  if ($[93] !== tenant.id) {
    t41 = stringifyQuery({
      tenant_id: tenant.id
    });
    $[93] = tenant.id;
    $[94] = t41;
  } else {
    t41 = $[94];
  }
  const t42 = `?${t41}`;
  let t43;
  if ($[95] !== t42) {
    t43 = <ViewSplitPaneContentHeader title="Latest events" actions={<Button outline={true} icon="open" to={{
      pathname: "/events",
      search: t42
    }}>Browse events</Button>} />;
    $[95] = t42;
    $[96] = t43;
  } else {
    t43 = $[96];
  }
  let t44;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      px: 612
    };
    $[97] = t44;
  } else {
    t44 = $[97];
  }
  let t45;
  if ($[98] !== latest_events_current_page) {
    t45 = {
      current_page: latest_events_current_page,
      page_size: LATEST_EVENTS_PAGE_SIZE,
      entity_name: "events"
    };
    $[98] = latest_events_current_page;
    $[99] = t45;
  } else {
    t45 = $[99];
  }
  let t46;
  if ($[100] !== latest_events_current_page || $[101] !== latest_events_response) {
    t46 = latest_events_response?.pagination?.next ? () => {
      setLatestEventsCursor({
        next: latest_events_response.pagination.next || undefined,
        prev: undefined
      });
      setLatestEventsCurrentPage(latest_events_current_page + 1);
    } : undefined;
    $[100] = latest_events_current_page;
    $[101] = latest_events_response;
    $[102] = t46;
  } else {
    t46 = $[102];
  }
  let t47;
  if ($[103] !== latest_events_current_page || $[104] !== latest_events_response?.pagination?.prev) {
    t47 = latest_events_current_page > 1 ? () => {
      setLatestEventsCursor({
        prev: latest_events_response?.pagination?.prev || undefined,
        next: undefined
      });
      setLatestEventsCurrentPage(latest_events_current_page - 1);
    } : undefined;
    $[103] = latest_events_current_page;
    $[104] = latest_events_response?.pagination?.prev;
    $[105] = t47;
  } else {
    t47 = $[105];
  }
  let t48;
  if ($[106] !== history) {
    t48 = event_id => {
      history.push(`/events/${event_id}`);
    };
    $[106] = history;
    $[107] = t48;
  } else {
    t48 = $[107];
  }
  const t49 = `/tenants/${tenant.id}/add-destination`;
  let t50;
  if ($[108] !== destination_types) {
    t50 = destination_types || [];
    $[108] = destination_types;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  if ($[110] !== history || $[111] !== tenant.id) {
    t51 = () => history.push(`/tenants/${tenant.id}`);
    $[110] = history;
    $[111] = tenant.id;
    $[112] = t51;
  } else {
    t51 = $[112];
  }
  let t52;
  if ($[113] !== mutate_destinations || $[114] !== t50 || $[115] !== t51 || $[116] !== tenant.id) {
    t52 = <CreateDestinationModal tenant_id={tenant.id} destination_types={t50} mutateDestinations={mutate_destinations} onClose={t51} />;
    $[113] = mutate_destinations;
    $[114] = t50;
    $[115] = t51;
    $[116] = tenant.id;
    $[117] = t52;
  } else {
    t52 = $[117];
  }
  let t53;
  if ($[118] !== t49 || $[119] !== t52) {
    t53 = <Route path={t49}>{t52}</Route>;
    $[118] = t49;
    $[119] = t52;
    $[120] = t53;
  } else {
    t53 = $[120];
  }
  return <><StyledViewSplitPaneLayout banner_active={is_degraded_banner_active}>{t30}<StyledViewSplitPaneContent>{t40}<StyledViewSplitPaneSection>{t43}<StyledCard overflow_hidden={true} h={t44}><Table headers={latest_event_headers} widths={latest_event_widths} rows={latest_event_rows} loading={latest_events_response_loading} current_count={latest_events.length} footer={true} pagination={t45} onNextPage={t46} onPreviousPage={t47} onRowSelected={t48} /></StyledCard></StyledViewSplitPaneSection></StyledViewSplitPaneContent></StyledViewSplitPaneLayout>{t53}</>;
};
function _temp(t0) {
  const {
    id
  } = t0;
  return id;
}
function _temp2(e) {
  return {
    time: new Date(e["Attempts.createdAt"]).getTime(),
    count: parseInt(e["Attempts.count"] || "0", 10),
    id: e["Attempts.destination_id"],
    dimension: e["Attempts.status"] || "success"
  };
}
function _temp3(t0) {
  const {
    id: id_0
  } = t0;
  return id_0;
}
function _temp4(acc_0, dt) {
  acc_0[dt.type] = dt;
  return acc_0;
}
function _temp5(field) {
  const last_part = field.path[field.path.length - 1];
  if (typeof last_part !== "string") {
    return false;
  }
  return POPULAR_FIELD_NAMES.includes(last_part.toLowerCase());
}
function _temp6(field_1) {
  return field_1 !== null;
}
function _temp7(field_2) {
  return field_2.display_name;
}
function _temp8(max_length, event) {
  return Math.max(max_length, event.topic.length);
}
function _temp9(t0) {
  const [key, value] = t0;
  return <ViewMetadataItem key={key} label={key}><Text mono={true} ellipsis={true}>{typeof value === "object" ? JSON.stringify(value) : String(value)}</Text></ViewMetadataItem>;
}